import React, { useCallback, useState } from "react";
import { FiUpload, FiX, FiImage } from "react-icons/fi";

const ImageUpload = ({
  images = [],
  onImagesChange,
  maxFiles = 5,
  label = "Upload Images",
  accept = "image/*",
  multiple = true,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (files) => {
    const fileList = Array.from(files);
    const remainingSlots = maxFiles - images.length;
    const filesToProcess = fileList.slice(0, remainingSlots);

    const newImages = filesToProcess.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isNew: true,
      name: file.name,
    }));

    onImagesChange([...images, ...newImages]);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      handleFileSelect(files);
    },
    [images, onImagesChange, maxFiles]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileSelect(files);
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-4">
      <label className="form-label">{label}</label>

      {/* Upload Area */}
      {images.length < maxFiles && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${
              isDragging
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
            }
          `}
        >
          <input
            type="file"
            multiple={multiple}
            accept={accept}
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />

          <FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-sm text-gray-600 mb-2">
            {isDragging
              ? "Drop the files here..."
              : "Drag & drop images here, or click to select"}
          </p>
          <p className="text-xs text-gray-400">
            PNG, JPG, GIF up to 5MB ({images.length}/{maxFiles} files)
          </p>
        </div>
      )}

      {/* Image Preview */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={
                    image.preview ||
                    (typeof image.url === "string" ? image.url : "")
                  }
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                  onLoad={() => {
                    // Clean up object URL if it's a new file
                    if (image.isNew && image.preview) {
                      // Don't revoke immediately, let it load first
                    }
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
              >
                <FiX size={14} />
              </button>
              {image.name && (
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {image.name}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {images.length >= maxFiles && (
        <p className="text-sm text-gray-500 text-center py-4">
          Maximum {maxFiles} files allowed
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
